import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../styles/AdminStyling/EditSponsor.css";

const EditSponsor = ({ activeTab }) => {
  const AddUser = `${process.env.PUBLIC_URL}/assets/logos/add-user.svg`;
  const EditIcon = `${process.env.PUBLIC_URL}/assets/logos/edit-icon.svg`;
  const DeleteIcon = `${process.env.PUBLIC_URL}/assets/logos/delete-icon.svg`;

  const [sponsors, setSponsors] = useState([]);
  const [filter, setFilter] = useState("");
  const [expandedSponsors, setExpandedSponsors] = useState([]);
  const [editingSponsorId, setEditingSponsorId] = useState(null);

  // Derive the filtered sponsors; adjust 'spname' if your sponsor object uses a different key.
  const filteredSponsors = sponsors.filter((sponsor) =>
    sponsor.spname.toLowerCase().includes(filter.toLowerCase())
  );

  // For adding a new sponsor
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({
    SPName: "",
    SPTimeline: [],
    SPDescription: "",
  });

  // For editing an existing sponsor
  const [editForm, setEditForm] = useState({
    SPID: null,
    SPName: "",
    SPTimeline: [],
    SPDescription: "",
  });

  // Track unsaved changes
  const [addDirty, setAddDirty] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Deletion confirmation (modal overlay)
  const [confirmDeleteSponsor, setConfirmDeleteSponsor] = useState(null);
  const [deleteInput, setDeleteInput] = useState("");

  // Fetch sponsors on mount
  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sponsors`
      );
      if (res.data.success) setSponsors(res.data.sponsors || []);
      else console.error("Failed to fetch sponsors:", res.data.message);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    }
  };

  // Warn user if unsaved changes
  useEffect(
    () => setUnsavedChanges(addDirty || editDirty),
    [addDirty, editDirty]
  );

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    if (unsavedChanges)
      window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsavedChanges]);

  // Expand/collapse sponsor card
  const toggleExpand = (spid) => {
    setExpandedSponsors((prev) => {
      if (prev.includes(spid)) return prev.filter((id) => id !== spid);
      return [spid];
    });
    if (expandedSponsors.includes(spid) && editingSponsorId === spid)
      cancelEdit();
  };

  // ------ Add Sponsor Logic ------
  const startAdd = () => {
    setAdding(true);
    setAddForm({ SPName: "", SPTimeline: [], SPDescription: "" });
    setAddDirty(false);
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddForm({ SPName: "", SPTimeline: [], SPDescription: "" });
    setAddDirty(false);
  };

  const handleAddChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
    setAddDirty(true);
  };

  // Timeline functions for add form
  const addTimelineItem = () => {
    setAddForm((prev) => ({ ...prev, SPTimeline: [...prev.SPTimeline, ""] }));
    setAddDirty(true);
  };
  const removeTimelineItem = (index) => {
    setAddForm((prev) => {
      const newTL = [...prev.SPTimeline];
      newTL.splice(index, 1);
      return { ...prev, SPTimeline: newTL };
    });
    setAddDirty(true);
  };
  const handleTimelineChange = (index, value) => {
    setAddForm((prev) => {
      const newTL = [...prev.SPTimeline];
      newTL[index] = value;
      return { ...prev, SPTimeline: newTL };
    });
    setAddDirty(true);
  };

  const saveNewSponsor = () => {
    if (!addForm.SPName.trim()) {
      alert("SPName is required!");
      return;
    }
    performAdd();
  };

  const performAdd = async () => {
    try {
      const timelineList = addForm.SPTimeline.map((x) => x.trim()).filter(
        (x) => x
      );
      const payload = {
        SPName: addForm.SPName.trim(),
        SPTimeline: timelineList.length > 0 ? timelineList : null,
        SPDescription: addForm.SPDescription || null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/addSponsor`,
        payload
      );
      if (res.data.success) {
        fetchSponsors();
        cancelAdd();
      } else {
        alert("Failed to add sponsor: " + res.data.message);
      }
    } catch (error) {
      console.error("Error adding sponsor:", error);
    }
  };

  // ------ Edit Sponsor Logic ------
  const enableEdit = (sponsor) => {
    if (!expandedSponsors.includes(sponsor.spid))
      setExpandedSponsors([sponsor.spid]);
    setEditingSponsorId(sponsor.spid);
    setEditForm({
      SPID: sponsor.spid,
      SPName: sponsor.spname || "",
      SPTimeline: sponsor.sptimeline ? [...sponsor.sptimeline] : [],
      SPDescription: sponsor.spdescription || "",
    });
    setEditDirty(false);
  };

  const cancelEdit = () => {
    setEditingSponsorId(null);
    setEditForm({
      SPID: null,
      SPName: "",
      SPTimeline: [],
      SPDescription: "",
    });
    setEditDirty(false);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    setEditDirty(true);
  };

  // Timeline functions for edit form
  const addTimelineItemEdit = () => {
    setEditForm((prev) => ({ ...prev, SPTimeline: [...prev.SPTimeline, ""] }));
    setEditDirty(true);
  };
  const removeTimelineItemEdit = (index) => {
    setEditForm((prev) => {
      const newTL = [...prev.SPTimeline];
      newTL.splice(index, 1);
      return { ...prev, SPTimeline: newTL };
    });
    setEditDirty(true);
  };
  const handleTimelineChangeEdit = (index, value) => {
    setEditForm((prev) => {
      const newTL = [...prev.SPTimeline];
      newTL[index] = value;
      return { ...prev, SPTimeline: newTL };
    });
    setEditDirty(true);
  };

  const saveEdits = () => {
    if (!editForm.SPName.trim()) {
      alert("SPName is required!");
      return;
    }
    performEdit();
  };

  const performEdit = async () => {
    try {
      const timelineList = editForm.SPTimeline.map((x) => x.trim()).filter(
        (x) => x
      );
      const payload = {
        SPID: editForm.SPID,
        SPName: editForm.SPName.trim(),
        SPTimeline: timelineList.length > 0 ? timelineList : null,
        SPDescription: editForm.SPDescription || null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/editSponsor`,
        payload
      );
      if (res.data.success) {
        fetchSponsors();
      } else {
        alert("Failed to edit sponsor: " + res.data.message);
      }
    } catch (error) {
      console.error("Error editing sponsor:", error);
    }
    cancelEdit();
  };

  // const confirmDelete = (sponsor) => {
  //   setConfirmDeleteSponsor(sponsor);
  //   setDeleteInput("");
  // };

  const performDelete = async () => {
    if (!confirmDeleteSponsor) return;
    try {
      const spid = confirmDeleteSponsor.spid;
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/deleteSponsor?spid=${spid}`
      );
      if (res.data.success) {
        fetchSponsors();
      } else {
        alert("Failed to delete sponsor: " + res.data.message);
      }
    } catch (error) {
      console.error("Error deleting sponsor:", error);
    }
    setConfirmDeleteSponsor(null);
    setDeleteInput("");
  };

  return (
    <div className="edit-sponsors-container">
      {/* <h2>Sponsor Manager</h2> */}

      {/* Add Sponsor Section */}
      <section className="add-sponsor-section">
        {/* <h3>Add Sponsor</h3> */}
        {!adding ? (
          <button className="add-user-button" onClick={startAdd}>
            <img
              src={activeTab === "sponsors" ? AddUser : AddUser}
              alt=""
              style={{ marginRight: "10px" }}
            />
            Add Sponsor
          </button>
        ) : (
          <div className="add-sponsor-form">
            <label>Sponsor Name (required)</label>
            <input
              type="text"
              placeholder="Sponsor Name"
              value={addForm.SPName}
              onChange={(e) => handleAddChange("SPName", e.target.value)}
            />
            <label>Member Since (optional)</label>
            {addForm.SPTimeline.length > 0 ? (
              <div className="field list-field">
                {addForm.SPTimeline.map((item, idx) => (
                  <div key={idx} className="list-item">
                    <input
                      type="text"
                      placeholder="Member Since event"
                      value={item}
                      onChange={(e) =>
                        handleTimelineChange(idx, e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="list-remove-btn"
                      onClick={() => removeTimelineItem(idx)}
                    >
                      &minus;
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="list-add-btn"
                  onClick={addTimelineItem}
                >
                  &#43;
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="add-item-btn"
                onClick={addTimelineItem}
              >
                + Add Member Since Item
              </button>
            )}
            <label>Description (optional)</label>
            <textarea
              placeholder="Sponsor Description"
              value={addForm.SPDescription}
              onChange={(e) => handleAddChange("SPDescription", e.target.value)}
            />
            <div className="item-actions">
              <button
                type="submit"
                className="save-btn"
                onClick={saveNewSponsor}
              >
                Add Sponsor
              </button>
              <button className="cancel-btn" onClick={cancelAdd}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Current Sponsors Section */}
      <section className="current-sponsors">
        <div className="filter-container">
          <label>Search Sponsor by Name:</label>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-input"
            placeholder="Enter sponsor name..."
          />
        </div>
        {/* <h3>Current Sponsors</h3> */}
        {filteredSponsors.length === 0 ? (
          <p>No sponsors found.</p>
        ) : (
          filteredSponsors.map((sponsor) => {
            const expanded = expandedSponsors.includes(sponsor.spid);
            return (
              <div key={sponsor.spid} className="sponsor-card">
                <div
                  className="sponsor-header"
                  onClick={() => toggleExpand(sponsor.spid)}
                >
                  <div className="sponsor-title">{sponsor.spname}</div>
                  <div className="sponsor-actions">
                    <img
                      alt="Edit"
                      className="user-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        enableEdit(sponsor);
                      }}
                      src={EditIcon}
                    />
                    <img
                      alt="Delete"
                      className="user-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteSponsor(sponsor);
                        setDeleteInput("");
                      }}
                      src={DeleteIcon}
                    />
                  </div>
                </div>
                {expanded && (
                  <div className="sponsor-details">
                    {editingSponsorId === sponsor.spid ? (
                      <div className="sponsor-edit-form">
                        <label>Sponsor Name (required)</label>
                        <input
                          type="text"
                          value={editForm.SPName}
                          onChange={(e) =>
                            handleEditChange("SPName", e.target.value)
                          }
                        />
                        <label>Member Since (optional)</label>
                        {editForm.SPTimeline.length > 0 ? (
                          <div className="field list-field">
                            {editForm.SPTimeline.map((item, idx) => (
                              <div key={idx} className="list-item">
                                <input
                                  type="text"
                                  placeholder="Member Since event"
                                  value={item}
                                  onChange={(e) =>
                                    handleTimelineChangeEdit(
                                      idx,
                                      e.target.value
                                    )
                                  }
                                />
                                <button
                                  type="button"
                                  className="list-remove-btn"
                                  onClick={() => removeTimelineItemEdit(idx)}
                                >
                                  &minus;
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="list-add-btn"
                              onClick={addTimelineItemEdit}
                            >
                              &#43;
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="add-item-btn"
                            onClick={addTimelineItemEdit}
                          >
                            + Add Member Since Item
                          </button>
                        )}
                        <label>Description (optional)</label>
                        <textarea
                          value={editForm.SPDescription}
                          onChange={(e) =>
                            handleEditChange("SPDescription", e.target.value)
                          }
                        />
                        <div className="item-actions">
                          <button
                            type="submit"
                            className="save-btn"
                            onClick={saveEdits}
                          >
                            Update Sponsor
                          </button>
                          <button className="cancel-btn" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="sponsor-info">
                        {sponsor.sptimeline && sponsor.sptimeline.length > 0 ? (
                          <div>
                            <strong>Member Since:</strong>
                            <ul className="timeline-list">
                              {sponsor.sptimeline.map((evt, idx) => (
                                <li key={idx}>{evt}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p>
                            <strong>Member Since:</strong> None
                          </p>
                        )}
                        <p>
                          <strong>Description:</strong>{" "}
                          {sponsor.spdescription || "None"}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>

      {/* Delete Confirmation Modal */}
      {confirmDeleteSponsor && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Confirm Deletion</h4>
            <p>
              Type <strong>{confirmDeleteSponsor.spname}</strong> to confirm
              deletion:
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
            />
            <div className="delete-actions">
              <button
                className="delete-confirm-btn"
                onClick={performDelete}
                disabled={deleteInput !== confirmDeleteSponsor.spname}
              >
                Delete
              </button>
              <button
                className="cancel-btn"
                onClick={() => setConfirmDeleteSponsor(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditSponsor;
